import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { MovementsList } from '@/common/components/MovementsList/MovementsList';
import { OperationsList } from '@/common/components/OperationsList/OperationsList';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { MARKET } from '@/common/constants/markets';
import { useMarket } from '@/common/hooks/useMarket';
import { useGetOperations } from '@/common/integrations/menhir/employee/operations/operations.hooks';
import { parseDate } from '@/common/utils/date';
import { useMovements } from '@/web/hooks/useMovements';
import { useLocalSearchParams } from 'expo-router';
import { ActivityFilters } from './ActivityFilters/ActivityFilters';
import {
  TActivityFilters,
  getInitialActivityFilters,
  getNumberOfActiveFilters,
} from './ActivityFilters/ActivityFilters.utils';
import { getMovementsParams, getOperationsUsage } from './ActivityPage.utils';

export function ActivityPage() {
  const { t } = useTranslation();
  const { slug } = useMarket();
  const searchParams =
    useLocalSearchParams<
      Pick<TActivityFilters, 'product' | 'benefit' | 'budget'>
    >();

  const [filters, setFilters] = useState<TActivityFilters>(
    getInitialActivityFilters(searchParams)
  );
  const [filtersVisible, setFiltersVisible] = useState(false);

  return (
    <PageLayout
      headerProps={{
        title: t('dashboard.activity.title'),
        actions: (
          <Button
            className={styles.filtersAction}
            variant="info"
            onPress={() => setFiltersVisible(!filtersVisible)}
            icon={<Icon icon="filter" />}
            size="sm"
          >
            {getNumberOfActiveFilters(filters)}
          </Button>
        ),
      }}
    >
      <ActivityFilters
        filters={filters}
        onChange={setFilters}
        visible={filtersVisible}
        onToggleVisibility={setFiltersVisible}
      />
      {slug === MARKET.PT ? (
        <MovementsActivity filters={filters} />
      ) : (
        <OperationsActivity filters={filters} />
      )}
    </PageLayout>
  );
}

type TActivityFiltersProps = {
  filters: TActivityFilters;
};

function MovementsActivity({ filters }: TActivityFiltersProps) {
  const params = getMovementsParams({
    product: filters.product,
    benefit: filters.benefit,
    budget: filters.budget,
  });
  const { movements, isLoading: isLoadingMovements } = useMovements({
    from: filters.from,
    to: filters.to,
    perPage: 'all',
    ...params,
  });

  return (
    <MovementsList
      movements={movements?.list ?? []}
      grouped
      loading={isLoadingMovements}
    />
  );
}

function OperationsActivity({ filters }: TActivityFiltersProps) {
  const { slug } = useMarket();
  const usage = getOperationsUsage({
    market: slug,
    product: filters.product,
  });
  const { operations, isLoading: isLoadingOperations } = useGetOperations({
    filters: {
      from: parseDate(filters.from).toISOString(),
      to: parseDate(filters.to).toISOString(),
      category: filters.benefit,
    },
    usage,
  });

  return (
    <OperationsList
      operations={operations?.list ?? []}
      grouped
      loading={isLoadingOperations}
    />
  );
}

export const styles = {
  filtersAction: 'min-w-0 self-end sm:hidden',
};
